import type { DriverDataErrorResponse } from "@App/apis/rest/ayvens/driverData/responses/driverDataErrorResponse";
import type { DriverDataApi } from "../interfaces";
import type { DriverDataSuccessResponse } from "../responses/driverDataSuccessResponse";
import type { FleetManagerSuccessResponse } from "@App/apis/rest/ayvens/driverData/responses/fleetManagerSuccessResponse";
import { DriverDataError } from "@App/apis/rest/ayvens/driverData/driverDataError";

/**
 * Service class for interacting with the Driver Data API.
 * Provides methods to fetch driver data and vehicle lists using JWT tokens.
 */
export class DriverDataService implements DriverDataApi {
  private readonly baseUrl: string;

  /**
   * Constructs an instance of DriverDataService.
   * @param {string} [baseUrl=''] - The base URL for the API (e.g., "http://localhost:3000").
   *                                If empty, relative paths will be used.
   */
  constructor(baseUrl = "") {
    this.baseUrl = baseUrl;
  }

  /**
   * Fetches driver data using a JWT token.
   * @param {string} token - The JWT token for authentication.
   * @returns {Promise<DriverDataSuccessResponse>} - Resolves with the driver data on success.
   * @throws {Error} - Throws an error for network issues or invalid responses.
   * @throws {TokenError} - Throws a TokenError for specific backend errors (e.g., 401, 404, 422).
   */
  public async getFromJwt(token: string): Promise<DriverDataSuccessResponse> {
    const tokenUrl = this.baseUrl
      ? `${this.baseUrl}/rest/repareo/frontend/v1/ayvens/driverData`
      : "/rest/repareo/frontend/v1/ayvens/driverData";

    let response: Response;
    try {
      response = await fetch(`${tokenUrl}/${token}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json", // Good practice to specify expected response type
        },
      });
    } catch (networkError) {
      // This catches errors from fetch itself (e.g., network down, DNS issue)
      // These are typically TypeError
      throw new Error(
        `Network error: Unable to reach the API. Original: ${(networkError as Error).message}`,
      );
    }

    if (response.status === 200) {
      try {
        return await response.json();
      } catch (jsonError) {
        // This catches errors from response.json() if the body is not valid JSON
        throw new Error(
          `Failed to parse successful API response. Original: ${(jsonError as Error).message}`,
        );
      }
    }

    // Handle Errors that the backend throws and rethrow with the data
    if ([401, 404, 422].includes(response.status)) {
      const data: DriverDataErrorResponse = await response.json();
      throw new DriverDataError(data);
    }

    // Handle other non-ok statuses (e.g., 400 Bad Request, 500 Internal Server Error)
    const errorText = await response
      .text()
      .catch(() => `Status ${response.status} with no error body.`);
    throw new Error(`API request failed with status ${response.status}: ${errorText}`);
  }

  /**
   * Fetches a paginated list of vehicles for a driver using a JWT token.
   * @param {string} token - The JWT token for authentication.
   * @param {number} [page=1] - The page number for pagination (default is 1).
   * @returns {Promise<FleetManagerSuccessResponse>} - Resolves with the vehicle list on success.
   * @throws {Error} - Throws an error for network issues or invalid responses.
   * @throws {TokenError} - Throws a TokenError for specific backend errors (e.g., 401, 404, 422).
   */
  public async getVehicleListFromJwt(
    token: string,
    page = 1,
  ): Promise<FleetManagerSuccessResponse> {
    const tokenUrl = `${this.baseUrl ? this.baseUrl : ""}/rest/repareo/frontend/v1/ayvens/fleetManager`;

    let response: Response;
    try {
      response = await fetch(`${tokenUrl}/${token}/vehicles?page=${page}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json", // Good practice to specify expected response type
        },
      });
    } catch (networkError) {
      // This catches errors from fetch itself (e.g., network down, DNS issue)
      // These are typically TypeError
      throw new Error(
        `Network error: Unable to reach the API. Original: ${(networkError as Error).message}`,
      );
    }

    if (response.status === 200) {
      try {
        return await response.json();
      } catch (jsonError) {
        // This catches errors from response.json() if the body is not valid JSON
        throw new Error(
          `Failed to parse successful API response. Original: ${(jsonError as Error).message}`,
        );
      }
    }

    // Handle Errors that the backend throws and rethrow with the data
    if ([401, 404, 422].includes(response.status)) {
      const data: DriverDataErrorResponse = await response.json();
      throw new DriverDataError(data);
    }

    // Handle other non-ok statuses (e.g., 400 Bad Request, 500 Internal Server Error)
    const errorText = await response
      .text()
      .catch(() => `Status ${response.status} with no error body.`);
    throw new Error(`Create Token request failed with status ${response.status}: ${errorText}`);
  }
}
